import type { ComponentProps, FC, PropsWithChildren } from 'react';
import React, { Fragment, useContext, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { VIEW_PAGE_TITLE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { getTitleContentProperty } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import {
	doesContentHaveEmojiPublished,
	getPublishedEmojiId,
} from '@confluence/emoji-title/entry-points/transformer.ts';
import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { getContentAppearancePublished } from '@confluence/content-appearance/entry-points/contentAppearance.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import {
	ReloadType,
	useQuickReloadSubscriber,
} from '@confluence/quick-reload/entry-points/subscription.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop';

import { CONTENT_TITLE_METRIC } from '../perf.config.js';
import {
	default as ContentHeaderUnifiedQuery,
	type ContentHeaderUnifiedQuery$data as ContentHeaderUnifiedQueryT,
	type ContentHeaderUnifiedQuery$variables as ContentHeaderUnifiedQueryVariables,
} from '../__generated__/ContentHeaderUnifiedQuery.graphql.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from '../defaultQueryVariables.js';

import { ContentTitleComponent } from './ContentTitleComponent.tsx';
import { ContentTitlePlaceholder } from './ContentTitlePlaceholder.js';
import {
	default as ContentTitleExternalQuery,
	type ContentTitleExternalQuery$data as ContentTitleExternalQueryT,
	type ContentTitleExternalQuery$variables as ContentTitleExternalQueryVariables,
} from './__generated__/ContentTitleExternalQuery.graphql.ts';

type ContentHeaderUnifiedQuery_content_PaginatedContentListWithChild_nodes_Content = NonNullable<
	NonNullable<NonNullable<ContentHeaderUnifiedQueryT>['content']>['nodes']
>[0];

import { confluenceSessionStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const deriveLookAndFeelFromData = (data) => {
	const { space } = data;

	if (!space) {
		return null;
	}

	const { lookAndFeel } = space;

	return {
		...lookAndFeel,
		headings: lookAndFeel.headings.reduce((obj, heading) => {
			obj[heading.key] = heading.value;
			return obj;
		}, {}),
	};
};

/**
 * Checks if a page is a Fabric page by looking at either metadata or editor property
 * @param {Object} contentNode - The content node from GraphQL query
 * @returns {boolean} - Whether the page is a Fabric page
 */
export const checkIsFabricPage = (contentNode) => {
	return contentNode?.metadata?.frontend?.fabricEditorSupported ?? false;
};

type PassThroughProps = Pick<ComponentProps<typeof ContentTitleComponent>, 'headingContainer'>;

type ContentTitleProps = {
	contentId: string;
	spaceKey: string;
	banner?: React.ReactNode;
	prefix?: string;
	hasCoverPicture?: boolean;
	pageWidthType?: string;
	isEmbeddedPage?: boolean;
};

export const ContentTitle: FC<PropsWithChildren<ContentTitleProps & PassThroughProps>> = ({
	spaceKey,
	banner,
	contentId,
	children = null,
	prefix,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	...passThroughProps
}) => {
	return (
		<Fragment>
			<ExperienceStart id={contentId} name={VIEW_PAGE_TITLE_EXPERIENCE} />
			<ErrorBoundary attribution={Attribution.BACKBONE}>
				<PageSegmentLoadStart key={contentId} metric={CONTENT_TITLE_METRIC} />
				<ContentTitleWrapper
					contentId={contentId}
					spaceKey={spaceKey}
					banner={banner}
					prefix={prefix}
					hasCoverPicture={hasCoverPicture}
					pageWidthType={pageWidthType}
					isEmbeddedPage={isEmbeddedPage}
					{...passThroughProps}
				>
					{children}
				</ContentTitleWrapper>
			</ErrorBoundary>
		</Fragment>
	);
};

const ContentTitleWrapper: FC<PropsWithChildren<ContentTitleProps & PassThroughProps>> = ({
	spaceKey,
	banner,
	contentId,
	children,
	prefix,
	hasCoverPicture,
	pageWidthType,
	isEmbeddedPage,
	...passThroughProps
}) => {
	/**
	 * Position of a fabric page title is determined by the appearance of the content:
	 * full width content is left aligned
	 * default (fixed width) is center aligned
	 */
	const shouldCenterFabricPage = (graphqlData) => {
		const contentAppearance = getContentAppearancePublished(graphqlData);
		return contentAppearance.appearance === ContentAppearanceType.DEFAULT;
	};

	const isCenteredTitle = (graphqlData) => {
		// TinyMCE content page titles are not centred.
		// Fabric content page titles are centred by default; but should be left-aligned if full-width.
		const isFabricPage = checkIsFabricPage(graphqlData);
		return isFabricPage ? shouldCenterFabricPage(graphqlData) : false;
	};

	const { isExternalShareRequest } = useContext(ExternalShareContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		loading,
		error,
		data: queryData,
		refetch,
	} = useQueryInterop<
		ContentTitleExternalQueryT | ContentHeaderUnifiedQueryT,
		ContentTitleExternalQueryVariables | ContentHeaderUnifiedQueryVariables
		// TODO: split this into two `useQueryInterop` statements; ideally, they should be in two different components. Otherwise TS typings get very messy
	>(isExternalShareRequest ? ContentTitleExternalQuery : ContentHeaderUnifiedQuery, {
		variables: {
			contentId,
			spaceKey,
			// Don't omit versionOverride, embeddedContentRender and limit
			// It's an Apollo bug that if preloader these needs to be match preloaded query
			// Also doesn't work if removing them from preloader variables HOT-109035
			versionOverride: null,
			embeddedContentRender: null,
			limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
			useNewContentTopper: true,
		},
		context: {
			single: true,
			allowOnExternalPage: true,
		},
	});

	const data = isExternalShareRequest
		? (queryData as ContentTitleExternalQueryT)?.singleContent
		: (queryData as ContentHeaderUnifiedQueryT)?.content?.nodes?.[0];

	const reload = useCallback(
		// Forcing refetch by passing dummy variable. Known Apollo bug (ノಠ益ಠ)ノ彡┻━┻, see page below:
		// https://pug.jira-dev.com/wiki/spaces/~683304838/pages/5971970200/Apollo+Client+-+weird+refetch+behavior
		// Reason using static cacheBuster: "":
		// ByLine component also makes ContentHeaderUnifiedQuery and refetch.
		// If using different cache buster values, the refetch in each component will make its owen refetch network call.
		// So setting a static value for this and ByLine component to make refetch network call only once.
		// And a static cache buster value can also force refetch to ignore cache of original query.
		// @ts-expect-error -- since `cacheBuster` isn't a known variable, we ignore this TS warning
		() => refetch({ contentId, cacheBuster: '' }),
		[refetch, contentId],
	);

	useQuickReloadSubscriber({
		name: 'title',
		types: [ReloadType.content],
		reload,
	});

	const title = data?.title || '';

	useEffect(() => {
		// Make sure that the GraphQL query has completed before firing any analytics.
		if (!loading && !error && title === '') {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					actionSubject: 'emptyPageTitle',
					action: 'shown',
					source: 'ContentTitleComponent',
					attributes: {
						contentId,
					},
				},
			}).fire();
		}
	}, [createAnalyticsEvent, title, loading, error, contentId]);

	useEffect(() => {
		if (
			!loading &&
			!error &&
			title &&
			expValEquals('cc_view_page_title_stored_in_session_storage', 'isEnabled', true)
		) {
			confluenceSessionStorageInstance.setItem(
				`${keys.CONFLUENCE_VIEW_PAGE_TITLE_PREFIX}${contentId}`,
				title,
			);
		}
	}, [title, loading, error, contentId]);

	// We don't check loading state here because it is ok to display staled data
	if (error) {
		// Displaying errors for content is handled other parts of the overall experience; the title doesn't need its own error state
		return <ErrorDisplay error={error} />;
	} else if (!data) {
		return <ContentTitlePlaceholder />;
	}

	const lookAndFeel = deriveLookAndFeelFromData(queryData);

	// NOTE: external share request doesn't allow to retrieve space details
	const spaceName = !isExternalShareRequest
		? (data as ContentHeaderUnifiedQuery_content_PaginatedContentListWithChild_nodes_Content)?.space
				?.name
		: '';

	const centerTitle = isCenteredTitle(data);

	const emojiId = getPublishedEmojiId(queryData);

	const hasEmoji = doesContentHaveEmojiPublished(
		isExternalShareRequest
			? (queryData as ContentTitleExternalQueryT)?.singleContent
			: (queryData as ContentHeaderUnifiedQueryT)?.content?.nodes?.[0],
	);

	const { titleContentProperties } = getTitleContentProperty(queryData);

	return (
		<Fragment>
			<ContentTitleComponent
				contentId={contentId}
				title={title}
				lookAndFeel={lookAndFeel}
				banner={banner}
				centerTitle={centerTitle}
				prefix={prefix}
				emojiId={emojiId}
				hasEmoji={hasEmoji}
				loading={loading}
				hasCoverPicture={hasCoverPicture}
				isPageContentFullWidth={
					pageWidthType === `"full-width"` ||
					// Ensure fallback from `max` appearance to `full-width` when editor_tinymce_full_width_mode and confluence_max_width_content_appearance are disabled
					// Remove this condition when cleaning up editor_tinymce_full_width_mode and confluence_max_width_content_appearance
					(pageWidthType === `"max"` &&
						!expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) &&
						!expValEquals('confluence_max_width_content_appearance', 'isEnabled', true))
				}
				isPageContentMaxWidth={
					pageWidthType === `"max"` &&
					(expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
						expValEquals('confluence_max_width_content_appearance', 'isEnabled', true))
				}
				isEmbeddedPage={isEmbeddedPage}
				spaceKey={spaceKey}
				contentType={
					(data as ContentHeaderUnifiedQuery_content_PaginatedContentListWithChild_nodes_Content)
						?.type ?? ''
				}
				titleContentProperties={titleContentProperties}
				{...passThroughProps}
			>
				{children}
			</ContentTitleComponent>
			<DocumentContentTitle
				contentTitle={title}
				contentSpace={spaceName || ''}
				titlePrefix={prefix}
			/>
		</Fragment>
	);
};

ContentTitle.propTypes = {
	spaceKey: PropTypes.string.isRequired,
	banner: PropTypes.node,
	contentId: PropTypes.string.isRequired,
	children: PropTypes.node,
	prefix: PropTypes.string,
	headingContainer: PropTypes.elementType,
};
ContentTitle.displayName = 'ContentTitle';
